import { useEffect, useMemo, useRef, useState } from 'react'
import { format as fnsFormat, isValid } from 'date-fns'
import { parseUTCTimestamp } from '@/utils/formatters'
import { CalendarIcon, Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import { parseDagInput } from './DagCellRenderer'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { formatInputValue } from '../../utils/SecondaryAttributesDrawer.utils'
import type { SecondaryFieldEditorProps } from '../../types/secondary-attributes-drawer.types'

/* ------------------------------------------------------------------ */
/*  ExtensibleDropdown – supports both selecting and typing custom     */
/* ------------------------------------------------------------------ */

function ExtensibleDropdown({
  value,
  options,
  onSelect,
  onCancel,
}: {
  value: string | null
  options: { value: string; display_label?: string | null }[]
  onSelect: (value: string | null) => void
  onCancel: () => void
}) {
  const [open, setOpen] = useState(true)
  const [search, setSearch] = useState('')
  const [customInput, setCustomInput] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const customInputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!search) return options
    const q = search.toLowerCase()
    return options.filter(
      o =>
        o.value.toLowerCase().includes(q) ||
        (o.display_label?.toLowerCase().includes(q) ?? false)
    )
  }, [options, search])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchInputRef.current?.focus())
    }
  }, [open])

  useEffect(() => {
    if (customInput) {
      requestAnimationFrame(() => customInputRef.current?.focus())
    }
  }, [customInput])

  if (customInput) {
    return (
      <Input
        ref={customInputRef}
        value={customValue}
        onChange={e => setCustomValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            if (customValue.trim()) onSelect(customValue.trim())
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setCustomInput(false)
            setCustomValue('')
            setOpen(true)
          }
        }}
        onBlur={() => {
          if (customValue.trim()) {
            onSelect(customValue.trim())
          } else {
            onCancel()
          }
        }}
        placeholder="Type custom value..."
        className="h-8 text-sm"
      />
    )
  }

  const displayLabel =
    options.find(o => o.value === value)?.display_label || value

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen)
        if (!nextOpen) onCancel()
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between text-sm font-normal cursor-pointer"
        >
          <span className="truncate">
            {value ? displayLabel : 'Select value...'}
          </span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <div className="p-2">
          <Input
            ref={searchInputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search..."
            className="h-8 text-sm"
          />
        </div>
        <div className="max-h-[200px] overflow-y-auto">
          {/* Empty option */}
          <div
            className="cursor-pointer flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted"
            onClick={() => onSelect(null)}
          >
            <Check
              className={cn(
                'h-3.5 w-3.5 shrink-0',
                value === null ? 'opacity-100' : 'opacity-0'
              )}
            />
            <span className="text-muted-foreground">-</span>
          </div>
          {filtered.map(opt => (
            <div
              key={opt.value}
              className="cursor-pointer flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted"
              onClick={() => onSelect(opt.value)}
            >
              <Check
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  value === opt.value ? 'opacity-100' : 'opacity-0'
                )}
              />
              {opt.display_label || opt.value}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-2 py-2 text-sm text-muted-foreground">
              No options found
            </div>
          )}
          {/* "Other..." option */}
          {!search && (
            <div
              className="cursor-pointer px-2 py-1.5 text-sm italic text-muted-foreground hover:bg-muted"
              onClick={() => {
                setOpen(false)
                setCustomInput(true)
              }}
            >
              Other...
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------ */
/*  SecondaryAttributesFieldEditor                                     */
/* ------------------------------------------------------------------ */

export function SecondaryAttributesFieldEditor({
  rowId,
  rowVersion,
  meta,
  value,
  dropdownOptions,
  onCellEdit,
  onTab,
  onExitEdit,
}: SecondaryFieldEditorProps) {
  const [inputValue, setInputValue] = useState(() =>
    formatInputValue(value, meta.format)
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const tabPressedRef = useRef(false)
  const selectionMadeRef = useRef(false)

  const isComputing = useDagTableStore(state =>
    state.computingCells.has(`${rowId}:${meta.id}`)
  )

  useEffect(() => {
    setInputValue(formatInputValue(value, meta.format))
  }, [value, meta.format])

  // Auto-focus and select text on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const commitInputValue = () => {
    const parsed = parseDagInput(inputValue, meta.format)
    if (Object.is(parsed, value)) {
      onExitEdit?.()
      return
    }
    onCellEdit(rowId, meta.id, parsed, rowVersion)
  }

  if (meta.dropdown_category && dropdownOptions?.length) {
    // EXTENSIBLE mode: searchable combobox with "Other..." custom input
    if (meta.dropdown_mode === 'EXTENSIBLE') {
      return (
        <ExtensibleDropdown
          value={value == null ? null : String(value)}
          options={dropdownOptions}
          onSelect={newValue => {
            if (Object.is(newValue, value)) {
              onExitEdit?.()
              return
            }
            onCellEdit(rowId, meta.id, newValue, rowVersion)
            onExitEdit?.()
          }}
          onCancel={() => onExitEdit?.()}
        />
      )
    }

    // FIXED mode: standard select
    const rawValue = value == null ? '__EMPTY__' : String(value)
    const matching = dropdownOptions.some(opt => opt.value === rawValue)
    const selectValue = matching ? rawValue : '__EMPTY__'

    return (
      <Select
        defaultOpen
        value={selectValue}
        onValueChange={newValue => {
          const parsed = newValue === '__EMPTY__' ? null : newValue
          if (Object.is(parsed, value)) {
            onExitEdit?.()
            return
          }
          selectionMadeRef.current = true
          onCellEdit(rowId, meta.id, parsed, rowVersion)
          onExitEdit?.()
        }}
        onOpenChange={open => {
          if (!open) {
            if (selectionMadeRef.current) {
              selectionMadeRef.current = false
              return
            }
            // Dropdown closed without selecting — exit edit mode
            onExitEdit?.()
          }
        }}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="Select value" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__EMPTY__">-</SelectItem>
          {dropdownOptions.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.display_label || option.value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  // Date format — render calendar popover
  if (meta.format === 'date') {
    const selectedDate =
      value != null && String(value) !== ''
        ? (() => {
            try {
              const d = parseUTCTimestamp(String(value))
              return isValid(d) ? d : undefined
            } catch {
              return undefined
            }
          })()
        : undefined

    const displayText = selectedDate
      ? fnsFormat(selectedDate, 'MM/dd/yyyy')
      : 'Select date'

    return (
      <Popover
        defaultOpen
        onOpenChange={open => {
          if (!open) onExitEdit?.()
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'h-8 w-full justify-start text-left text-sm font-normal cursor-pointer',
              !selectedDate && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {displayText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                const iso = fnsFormat(date, 'yyyy-MM-dd')
                onCellEdit(rowId, meta.id, iso, rowVersion)
              } else {
                onCellEdit(rowId, meta.id, null, rowVersion)
              }
              onExitEdit?.()
            }}
          />
          <div className="border-t px-3 py-2">
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={() => {
                onCellEdit(rowId, meta.id, null, rowVersion)
                onExitEdit?.()
              }}
            >
              Clear
            </button>
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  const isNumeric = [
    'currency',
    'currency_3dp',
    'percent',
    'number',
    'integer',
  ].includes(meta.format)

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitInputValue()
    } else if (event.key === 'Tab') {
      event.preventDefault()
      tabPressedRef.current = true
      commitInputValue()
      onTab?.(event.shiftKey)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      onExitEdit?.()
    }
  }

  const handleBlur = () => {
    if (tabPressedRef.current) {
      tabPressedRef.current = false
      return
    }
    commitInputValue()
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type={isNumeric ? 'number' : 'text'}
        step={meta.format === 'integer' ? '1' : '0.01'}
        value={inputValue}
        onChange={event => {
          const next = event.target.value
          if (meta.format !== 'integer' && next.includes('.')) {
            const [intPart, decPart] = next.split('.')
            if (decPart.length > 2) {
              setInputValue(`${intPart}.${decPart.slice(0, 2)}`)
              return
            }
          }
          setInputValue(next)
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-8 text-sm"
      />
      {isComputing && (
        <Loader2 className="pointer-events-none absolute right-2 top-2 h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  )
}
